import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, firstValueFrom, Observable, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { GAME_ID } from '../global/game-id';
import { FormConfig } from '../admin/config/base-form.config';
import { AppEvent, CoOrganiserDetail } from './models';
import { EventService } from './event.service';
import { UserService } from '../global/userService/user-service';
import { EVENT_FORM_CONFIG } from './event-create.page';
import { ImageService } from '../global/images/image.service';

@Component({
    standalone: false,
    selector: 'abs-event-edit',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.EDIT_TITLE' | translate">
            @if (organiserDisplayName()) {
                <ion-item lines="none">
                    <ion-label>
                        <h3>{{ 'GLOBAL.EVENTS.DETAIL.ORGANISER' | translate }}</h3>
                        <p>{{ organiserDisplayName() }}</p>
                    </ion-label>
                </ion-item>
            }

            <div class="banner-upload-section">
                <p class="banner-label">Event Banner Image <small>(max 2 MB — JPEG, PNG, WebP)</small></p>
                @if (bannerPreviewUrl) {
                    <div class="banner-preview">
                        <img [src]="bannerPreviewUrl" alt="Event banner preview" class="banner-preview-img" />
                        <div class="banner-actions">
                            <button class="banner-btn" (click)="bannerFileInput.click()" [disabled]="bannerUploading()">
                                <ion-icon name="image-outline"></ion-icon>
                                Change
                            </button>
                            <button class="banner-btn banner-btn--remove" (click)="removeBanner()" [disabled]="bannerUploading()">
                                <ion-icon name="trash-outline"></ion-icon>
                                Remove
                            </button>
                        </div>
                    </div>
                } @else {
                    <button class="banner-btn banner-btn--add" (click)="bannerFileInput.click()" [disabled]="bannerUploading()">
                        @if (bannerUploading()) {
                            <ion-spinner name="crescent"></ion-spinner>
                            Uploading&hellip;
                        } @else {
                            <ion-icon name="image-outline"></ion-icon>
                            Add Event Banner
                        }
                    </button>
                }
                <input #bannerFileInput type="file" accept="image/*" style="display:none" (change)="onBannerSelected($event)" />
                @if (bannerError) {
                    <p class="banner-error">{{ bannerError }}</p>
                }
            </div>

            <div class="banner-upload-section">
                <p class="banner-label">Event Pack <small>(PDF, max 10 MB)</small></p>
                @if (eventPackFileId) {
                    <div class="banner-actions">
                        <a class="banner-btn" [href]="imageService.url(eventPackFileId)" target="_blank">
                            <ion-icon name="document-outline"></ion-icon>
                            View PDF
                        </a>
                        <button class="banner-btn" (click)="eventPackFileInput.click()" [disabled]="eventPackUploading()">
                            <ion-icon name="document-outline"></ion-icon>
                            Replace
                        </button>
                        <button class="banner-btn banner-btn--remove" (click)="removeEventPack()" [disabled]="eventPackUploading()">
                            <ion-icon name="trash-outline"></ion-icon>
                            Remove
                        </button>
                    </div>
                } @else {
                    <button class="banner-btn banner-btn--add" (click)="eventPackFileInput.click()" [disabled]="eventPackUploading()">
                        @if (eventPackUploading()) {
                            <ion-spinner name="crescent"></ion-spinner>
                            Uploading&hellip;
                        } @else {
                            <ion-icon name="document-outline"></ion-icon>
                            Upload Event Pack
                        }
                    </button>
                }
                <input
                    #eventPackFileInput
                    type="file"
                    accept="application/pdf"
                    style="display:none"
                    (change)="onEventPackSelected($event)"
                />
                @if (eventPackError) {
                    <p class="banner-error">{{ eventPackError }}</p>
                }
            </div>

            @if (editFormConfig$ | async; as editFormConfig) {
                <abs-form
                    [config]="editFormConfig.config"
                    [value]="editFormConfig.event"
                    (formSubmit)="onSubmit(editFormConfig.eventId, $event.formValue)"
                >
                    <abs-co-organiser-picker
                        [coOrganiserDetails]="coOrganiserDetails"
                        (coOrganisersChange)="coOrganiserIds = $event"
                    ></abs-co-organiser-picker>
                    @if (saveError) {
                        <div class="gutter-padding">
                            <ion-item color="danger" lines="none">
                                <ion-label class="ion-text-wrap">{{ saveError }}</ion-label>
                            </ion-item>
                        </div>
                    }
                    <div class="buttons">
                        <ion-button type="submit">{{ 'GLOBAL.SAVE' | translate }}</ion-button>
                        <ion-button color="danger" fill="outline" (click)="confirmDelete(editFormConfig.eventId)">
                            {{ 'GLOBAL.EVENTS.DELETE' | translate }}
                        </ion-button>
                    </div>
                </abs-form>
            }
        </abs-page-layout>
    `,
    styles: [
        `
            .banner-upload-section {
                padding: 16px;
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .banner-label {
                margin: 0 0 10px;
                font-weight: 600;
                font-size: 0.85rem;
            }

            .banner-label small {
                font-weight: 400;
                color: var(--ion-color-medium, #92949c);
            }

            .banner-preview {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }

            .banner-preview-img {
                width: 100%;
                max-height: 180px;
                object-fit: cover;
                border-radius: 6px;
                border: 1px solid var(--ion-color-light-shade, #d7d8da);
                display: block;
            }

            .banner-actions {
                display: flex;
                gap: 8px;
            }

            .banner-btn {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                padding: 7px 14px;
                border-radius: 6px;
                border: 1px solid var(--ion-color-primary, #3880ff);
                background: var(--ion-color-primary, #3880ff);
                color: #fff;
                font-size: 0.85rem;
                font-weight: 500;
                cursor: pointer;
            }

            .banner-btn:disabled {
                opacity: 0.5;
                cursor: default;
            }

            .banner-btn--remove {
                border-color: var(--ion-color-danger, #eb445a);
                background: var(--ion-color-danger, #eb445a);
            }

            .banner-btn--add {
                border-style: dashed;
                background: transparent;
                color: var(--ion-color-primary, #3880ff);
            }

            .banner-error {
                margin: 8px 0 0;
                font-size: 0.82rem;
                color: var(--ion-color-danger, #eb445a);
            }

            .buttons {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
        `
    ]
})
export class EventEditPage implements OnInit {
    private eventService = inject(EventService);
    private userService = inject(UserService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private alertCtrl = inject(AlertController);
    private translate = inject(TranslateService);
    protected imageService = inject(ImageService);
    gameId = inject(GAME_ID);
    formConfig$ = inject(EVENT_FORM_CONFIG);

    organiserDisplayName = computed(() => this.userService.user()?.displayName);

    editFormConfig$: Observable<{ config: FormConfig; eventId: string; event: AppEvent }>;
    coOrganiserDetails: CoOrganiserDetail[] = [];
    coOrganiserIds: string[] = [];
    saveError: string | null = null;
    deleting = signal(false);

    imageId: string | null = null;
    bannerPreviewUrl: string | null = null;
    bannerUploading = signal(false);
    bannerError: string | null = null;

    eventPackFileId: string | null = null;
    eventPackUploading = signal(false);
    eventPackError: string | null = null;

    private eventLoaded = false;

    ngOnInit() {
        const event$ = this.route.paramMap.pipe(
            map((params) => params.get('eventId')),
            switchMap((id) => this.eventService.getEvent(id)),
            tap((event) => {
                if (!this.eventLoaded) {
                    this.eventLoaded = true;
                    this.coOrganiserDetails = event.coOrganiserDetails || [];
                    this.coOrganiserIds = event.coOrganisers || [];
                    this.imageId = event.imageId || null;
                    this.bannerPreviewUrl = event.imageId ? this.imageService.url(event.imageId) : null;
                    this.eventPackFileId = event.eventPackFileId || null;
                }
            })
        );

        this.editFormConfig$ = combineLatest([this.formConfig$, event$]).pipe(
            map(([formConfig, event]) => ({
                config: formConfig,
                eventId: event._id,
                event
            }))
        );
    }

    onBannerSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        this.bannerError = null;
        this.bannerUploading.set(true);
        this.imageService.uploadEventBanner(this.gameId, file).subscribe({
            next: (res) => {
                this.imageId = res.id;
                this.bannerPreviewUrl = this.imageService.url(res.id);
                this.bannerUploading.set(false);
            },
            error: (err) => {
                this.bannerError = err?.error?.message || 'Upload failed. Please check the file and try again.';
                this.bannerUploading.set(false);
            }
        });

        // Reset so the same file can be re-selected if needed
        input.value = '';
    }

    removeBanner() {
        this.imageId = null;
        this.bannerPreviewUrl = null;
        this.bannerError = null;
    }

    onEventPackSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        this.eventPackError = null;
        this.eventPackUploading.set(true);
        this.imageService.uploadEventPack(this.gameId, file).subscribe({
            next: (res) => {
                this.eventPackFileId = res.id;
                this.eventPackUploading.set(false);
            },
            error: (err) => {
                this.eventPackError = err?.error?.message || 'Upload failed. Only PDF files up to 10 MB are accepted.';
                this.eventPackUploading.set(false);
            }
        });

        input.value = '';
    }

    removeEventPack() {
        this.eventPackFileId = null;
        this.eventPackError = null;
    }

    onSubmit(eventId: string, formValue: Partial<AppEvent>) {
        this.saveError = null;
        const updates: Partial<AppEvent> = {
            ...formValue,
            coOrganisers: this.coOrganiserIds,
            imageId: this.imageId,
            eventPackFileId: this.eventPackFileId
        };
        this.eventService.updateEvent(eventId, updates).subscribe({
            next: () => {
                this.router.navigate(['..'], { relativeTo: this.route });
            },
            error: (err) => {
                this.saveError = err?.error?.message || 'An error occurred while saving. Please check all fields and try again.';
            }
        });
    }

    async confirmDelete(eventId: string) {
        const registrations = await firstValueFrom(this.eventService.listRegistrations(eventId));
        const hasRegistrations = (registrations?.length ?? 0) > 0;
        const messageKey = hasRegistrations
            ? 'GLOBAL.EVENTS.DELETE_CONFIRM_MESSAGE_HAS_REGISTRATIONS'
            : 'GLOBAL.EVENTS.DELETE_CONFIRM_MESSAGE_NO_REGISTRATIONS';

        const alert = await this.alertCtrl.create({
            header: this.translate.instant('GLOBAL.EVENTS.DELETE_CONFIRM_TITLE'),
            message: this.translate.instant(messageKey),
            buttons: [
                { text: this.translate.instant('GLOBAL.CANCEL'), role: 'cancel' },
                {
                    text: this.translate.instant('GLOBAL.EVENTS.DELETE'),
                    cssClass: 'alert-button-danger',
                    handler: () => {
                        this.deleting.set(true);
                        this.eventService.deleteEvent(eventId).subscribe({
                            next: () => {
                                this.router.navigate(['../..'], { relativeTo: this.route });
                            },
                            error: (err) => {
                                this.deleting.set(false);
                                this.saveError = err?.error?.message || 'Failed to delete event.';
                            }
                        });
                    }
                }
            ]
        });
        await alert.present();
    }
}
